'use client';
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@ui/icons';
import { Checkbox, ButtonLink } from '@web/atoms';
import { ResponsiveImage } from '@web/molecules';
import emptyBowl from '../../images/product-photos/pack-pngs/empty-bowl-icon.png';
import {
  StyledImageDropdown,
  DropdownHeader,
  DropdownButton,
  DropdownMenu,
  IconContainer,
  DropdownContentContainer,
  ContentHeaderContainer,
  HeaderText,
  HelperText,
  StyledDropdownImageItem,
  DropdownItemContent,
  ContentImage,
  ContentText,
  ContentTextContainer,
  ContentLink,
  SelectionRow,
  SelectedText,
  RedDot,
  AllergyWarningText,
  LimitWarningText,
  NoSelectionText,
  ImageStackContainer,
  StackedImageWrapper,
} from './styles.js';

export const ImageDropdown = ({
  heading,
  helperText,
  noSelectionImageSrc,
  defaultText = 'No items selected',
  maxSelections = 4,
  defaultSelectedIds = [],
  children,
  onClickMenuButton,
  onChange,
  onDetailsClick,
}) => {
  const [openMenu, setOpenMenu] = useState(false);
  const [selectedIds, setSelectedIds] = useState(defaultSelectedIds);

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOpenMenu = () => {
    if (!openMenu) {
      onClickMenuButton && onClickMenuButton();
    }
    setOpenMenu(!openMenu);
  };

  const handleItemToggle = id => {
    let newSelectedIds = selectedIds;

    if (selectedIds.includes(id)) {
      newSelectedIds = selectedIds.filter(selectedId => selectedId !== id);
    } else {
      if (selectedIds.length < maxSelections) {
        newSelectedIds = [...selectedIds, id];
      }
    }

    setSelectedIds(newSelectedIds);

    if (onChange) {
      onChange(newSelectedIds);
    }
  };

  const getSelectedText = () => {
    if (selectedIds.length === 0)
      return <NoSelectionText>{defaultText}</NoSelectionText>;

    const selectedLabels = React.Children.toArray(children)
      .filter(child => selectedIds.includes(child.props.id))
      .map(child => child.props.summaryLabel || child.props.heading);

    return selectedLabels.join(', ');
  };

  const getSelectedImages = () => {
    return React.Children.toArray(children)
      .filter(child => selectedIds.includes(child.props.id))
      .map(child => ({
        id: child.props.id,
        imageSrc: child.props.imageSrc,
        altText: child.props.altText,
      }));
  };

  const isLimitReached = selectedIds.length >= maxSelections;
  const selectedImages = getSelectedImages();

  return (
    <StyledImageDropdown ref={dropdownRef}>
      <DropdownHeader onClick={handleOpenMenu}>
        <DropdownContentContainer>
          <ContentHeaderContainer>
            {heading && <HeaderText>{heading}</HeaderText>}
            {helperText && <HelperText>{helperText}</HelperText>}
          </ContentHeaderContainer>
          <SelectionRow>
            <ImageStackContainer>
              {selectedIds.length === 0 ? (
                <ResponsiveImage
                  src={noSelectionImageSrc || emptyBowl.src}
                  alt={'Selection box is empty'}
                  height={48}
                  width={75}
                />
              ) : (
                selectedImages.map((image, index) => (
                  <StackedImageWrapper key={image.id} $index={index}>
                    <ResponsiveImage
                      src={image.imageSrc}
                      alt={image.altText}
                      height={48}
                      width={75}
                    />
                  </StackedImageWrapper>
                ))
              )}
            </ImageStackContainer>
            <SelectedText>{getSelectedText()}</SelectedText>
          </SelectionRow>
        </DropdownContentContainer>
        <DropdownButton>
          <IconContainer>
            {openMenu ? (
              <Icon
                iconName="chevron-up"
                alt=""
                size={1}
                id="up_dropdown_menu"
              />
            ) : (
              <Icon
                iconName="chevron-down"
                alt=""
                size={1}
                id="down_dropdown_menu"
              />
            )}
          </IconContainer>
        </DropdownButton>
      </DropdownHeader>
      <DropdownMenu $isOpen={openMenu}>
        {React.Children.map(children, child => {
          if (React.isValidElement(child)) {
            const isSelected = selectedIds.includes(child.props.id);
            const isAllergen = child.props.isAllergen;
            const isLimitDisabled =
              !isSelected && isLimitReached && !isAllergen;
            const isDisabled = isAllergen || isLimitDisabled;

            return React.cloneElement(child, {
              checked: isSelected,
              disabled: isDisabled,
              isLimitDisabled: isLimitDisabled,
              isAllergen: isAllergen,
              onDetailsClick: onDetailsClick,
              onChange: () => !isDisabled && handleItemToggle(child.props.id),
            });
          }
          return child;
        })}
      </DropdownMenu>
    </StyledImageDropdown>
  );
};

ImageDropdown.propTypes = {
  heading: PropTypes.string,
  helperText: PropTypes.string,
  noSelectionImageSrc: PropTypes.string,
  defaultText: PropTypes.string,
  maxSelections: PropTypes.number,
  defaultSelectedIds: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node,
  onClickMenuButton: PropTypes.func,
  onChange: PropTypes.func,
  onDetailsClick: PropTypes.func,
};

export const DropdownImageItem = ({
  id,
  heading,
  summaryLabel,
  type,
  linkText,
  link,
  imageSrc,
  altText,
  checked,
  disabled,
  isLimitDisabled,
  isAllergen,
  onChange,
  onDetailsClick,
}) => {
  const renderBottomContent = () => {
    if (isAllergen) {
      return (
        <AllergyWarningText>
          <RedDot /> Unavailable due to allergy concern
        </AllergyWarningText>
      );
    }

    if (isLimitDisabled) {
      return (
        <LimitWarningText>
          Remove a selection to choose this item
        </LimitWarningText>
      );
    }

    return (
      <ButtonLink
        variant="hero.plum"
        onClick={e => {
          e.stopPropagation();
          if (onDetailsClick) {
            onDetailsClick({ id, link, type }); //* May not use a link but if we do, we can pass it through the onDetailsClick handler
          }
        }}
      >
        {linkText}
      </ButtonLink>
    );
  };

  return (
    <StyledDropdownImageItem $disabled={disabled} $isAllergen={isAllergen}>
      <DropdownItemContent onClick={onChange}>
        <div onClick={e => e.stopPropagation()}>
          <Checkbox
            key={id}
            name={heading}
            checked={checked}
            disabled={disabled}
            onChange={onChange}
          />
        </div>

        <ContentImage>
          <ResponsiveImage
            src={imageSrc}
            alt={altText}
            height={48}
            width={65}
          />
        </ContentImage>
        <ContentTextContainer>
          <ContentText>{heading}</ContentText>
          <ContentLink>{renderBottomContent()}</ContentLink>
        </ContentTextContainer>
      </DropdownItemContent>
    </StyledDropdownImageItem>
  );
};

DropdownImageItem.propTypes = {
  id: PropTypes.string.isRequired,
  heading: PropTypes.string.isRequired,
  summaryLabel: PropTypes.string,
  type: PropTypes.string,
  linkText: PropTypes.string.isRequired,
  link: PropTypes.string,
  imageSrc: PropTypes.string.isRequired,
  altText: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  isLimitDisabled: PropTypes.bool,
  isAllergen: PropTypes.bool,
  onChange: PropTypes.func,
  onDetailsClick: PropTypes.func,
};
